import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import AlertBar from "../utils/AlertBar";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";
import { useTitle } from "../utils/useTitle";
import { useAnalyticsEventTracker } from "../utils/useAnalyticsEventTracker";

const SendConfirmationEmail = () => {
  useTitle("Confirmation Email");
  const eventTracker = useAnalyticsEventTracker();

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState({
    email: false,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  function handleIsSuccessState() {
    setIsSuccess(false);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the error for the current field when the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);
    // Validate form fields
    const formErrors = {};
    Object?.keys(formData).forEach((key) => {
      if (!formData[key]) {
        formErrors[key] = true;
      }
    });

    if (Object?.keys(formErrors).length > 0) {
      // If there are errors, set the state to show errors
      setErrors(formErrors);
      setErrorMessage(t("auth.alertMsg.error1"));
      setIsLoading(false);
      return;
    }

    // Placeholder for the API request logic
    const url = `${API_URL}/auth/sendVerificationEmail`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      console.log(data);

      if (data.status === "success") {
        setIsSuccess(true);
        setSuccessMessage(t("auth.alertMsg.success3"));
        eventTracker("confirmation_email_request", {
          user_id: data.user.userId,
        });
      } else {
        setErrors({ error: true });
        setErrorMessage(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setErrors({ error: true });
      setErrorMessage(t("auth.alertMsg.error8"));
    } finally {
      // Reset form data and errors after successful submission or error
      setFormData({
        email: "",
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container>
        {/* Left Side (8 columns) */}
        <Grid item xs={6} container justify="center" alignItems="center">
          <Grid
            container
            direction="column"
            spacing={3}
            style={{ maxWidth: "800px", padding: "20px", marginLeft: "70px" }}
          >
            <Grid item>
              <h1>{t("auth.header.emailConfirmation")}</h1>
              <Typography variant="h5" gutterBottom>
                {t("auth.msg.emailConfirmation")}
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                label={t("auth.email")}
                type="email"
                variant="outlined"
                fullWidth
                required
                value={formData.email}
                onChange={handleInputChange}
                name="email"
                error={errors.email}
                helperText={errors.email && t("auth.requiredMsg.email")}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                style={{ height: "55px" }}
                onClick={handleFormSubmit}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("auth.btnText.emailConfirmation")
                )}
              </Button>
            </Grid>

            <Grid item></Grid>
          </Grid>
        </Grid>

        {/* Right Side (6 columns) */}
        <Grid item xs={6} style={{ overflow: "hidden" }}>
          <div style={{ width: "100%", height: "100vh" }}>
            <img
              src="assets/images/banner1.jpg"
              alt="Signin"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </Grid>
      </Grid>

      <AlertBar
        open={Object.values(errors).some((error) => error)}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors({})}
      />

      <AlertBar
        open={isSuccess}
        message={successMessage}
        severity={"success"}
        onClose={handleIsSuccessState}
      />
    </>
  );
};

export default SendConfirmationEmail;
